/* eslint-disable react/no-array-index-key */
import { useFormContext } from 'react-hook-form';

import Grid2 from '@thirdparty-framework/mui/material/Grid';
import TextField from './TextField';
import ReadOnly from './ReadOnly';
import TextArea from './TextArea';
import DatePicker from './DatePicker';
import NumericTextField from './NumericTextField';
import Switch from './Switch';
import FieldWrapperVerticalV2 from './FieldWrapperVerticalV2';
import { useMasterData } from '../../providers/AppDataProvider/index';
import { useLocalization } from '../../providers/LocalizationProvider';

function FieldGridRenderer({ schema, renderField }) {
  if (!schema || !Array.isArray(schema)) return null;

  // Group fields by row
  const rows = schema.reduce((acc, field) => {
    acc[field.row] = acc[field.row] || [];
    acc[field.row].push(field);
    return acc;
  }, {});

  // Sort rows by row number
  const sortedRows = Object.keys(rows)
    .sort((a, b) => Number(a) - Number(b))
    .map(rowNum => rows[rowNum]);

  return sortedRows.map((fieldsInRow, rowIdx) => {
    const isLastRow = rowIdx === sortedRows.length - 1;
    // const md = fieldsInRow?.width ? fieldsInRow?.width : 'auto';
    return (
      <Grid2 container key={rowIdx} gap={2} sx={{ mb: isLastRow ? 0 : 2.5 }}>
        {fieldsInRow.map((field, colIdx) => {
          if (field.control === 'hidden') {
            return renderField(field);
          }
          return (
            <Grid2 xs={12} md="grow" key={colIdx}>
              {renderField(field)}
            </Grid2>
          );
        })}
      </Grid2>
    );
  });
}

function FieldGeneratorV2({
  schema,
  customComponents = {},
  permissions = {},
  forceInitializeForm = () => undefined,
  showDirtyFields = false,
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const { formatMessage } = useLocalization();
  const masterData = useMasterData();

  /**
   * Renders a schema of fields with row/column layout.
   * Each row is wrapped in a <Grid2 container>, and each field is rendered in a <Grid2 md={12/totalColumns}>.
   */

  const renderField = field => {
    const placeHolderText = field?.placeholder?.localeId
      ? formatMessage({
          id: field?.placeholder?.localeId,
          defaultMessage: field?.placeholder?.defaultMessage,
        })
      : '';

    let commonProps = {
      id: field?.name,
      permission: field?.permission || null,
      placeholder: placeHolderText,
      error: errors[field?.name]?.message || false,
      ...register(field?.name),
    };

    const commonFieldWrapperProps = {
      field,
      errors,
      customComponents,
      showDirtyFields,
    };

    switch (field?.control) {
      case 'subsection': {
        const elements = FieldGridRenderer({
          schema: field?.children,
          renderField,
        });
        if (Array.isArray(elements) && elements?.length === 0) {
          return null;
        }
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            {elements}
          </FieldWrapperVerticalV2>
        );
      }
      case 'text': {
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <TextField {...commonProps} />
          </FieldWrapperVerticalV2>
        );
      }
      case 'date': {
        let commonDateProps = {};
        if (field?.disablePast) {
          commonDateProps = {
            ...commonDateProps,
            disablePast: field?.disablePast,
          };
        }
        if (field?.disableFuture) {
          commonDateProps = {
            ...commonDateProps,
            disableFuture: field?.disableFuture,
          };
        }
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <DatePicker {...commonProps} {...commonDateProps} />
          </FieldWrapperVerticalV2>
        );
      }
      case 'textarea': {
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <TextArea {...commonProps} rows={field.rows || 4} />
          </FieldWrapperVerticalV2>
        );
      }

      case 'number': {
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <NumericTextField
              {...commonProps}
              rows={field.rows || 4}
              positiveNumber={field?.positiveNumber || false}
              allowNull={field?.allowNull || false}
            />
          </FieldWrapperVerticalV2>
        );
      }

      case 'switch': {
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <Switch {...commonProps} />
          </FieldWrapperVerticalV2>
        );
      }

      case 'readonly': {
        return (
          <FieldWrapperVerticalV2
            key={field?.name}
            {...commonFieldWrapperProps}>
            <ReadOnly id={field?.name} {...register(field?.name)} />
          </FieldWrapperVerticalV2>
        );
      }

      case 'hidden': {
        return (
          <input
            type="hidden"
            key={field?.name}
            id={field?.name}
            {...register(field?.name)}
          />
        );
      }

      default: {
        const CustomComponent = customComponents[field.control];

        // Passing certain additonal features to required components
        // due to {...props} it will render in custom components on UI
        switch (field.control) {
          case 'Dispatch':
          case 'AddressValidator':
            commonProps = {
              ...commonProps,
              formPermissions: permissions,
            };
            break;
          case 'Claims':
            commonProps = {
              ...commonProps,
              forceInitializeForm,
            };
            break;
          default:
        }

        if (CustomComponent) {
          return (
            <FieldWrapperVerticalV2
              key={field?.name}
              {...commonFieldWrapperProps}>
              <CustomComponent {...commonProps} field={field} />
            </FieldWrapperVerticalV2>
          );
        }

        const CustomComponentConfig =
          customComponents.CustomComponentConfigs(masterData)?.[
            field.control
          ] || {};

        if (CustomComponentConfig) {
          switch (CustomComponentConfig?.control) {
            case 'autocomplete': {
              const { AutoCompleteWrapper } = customComponents;
              return (
                <FieldWrapperVerticalV2
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <AutoCompleteWrapper
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVerticalV2>
              );
            }
            case 'autocompletev2': {
              const { AutoCompleteWrapperV2 } = customComponents;
              return (
                <FieldWrapperVerticalV2
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <AutoCompleteWrapperV2
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVerticalV2>
              );
            }
            case 'SelectStatic': {
              const { SelectStatic } = customComponents;
              return (
                <FieldWrapperVerticalV2
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <SelectStatic
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVerticalV2>
              );
            }
            case 'StatesSelector': {
              const { StatesSelector } = customComponents;
              return (
                <FieldWrapperVerticalV2
                  key={field?.name}
                  {...commonFieldWrapperProps}>
                  <StatesSelector
                    {...commonProps}
                    field={field}
                    customConfig={CustomComponentConfig}
                  />
                </FieldWrapperVerticalV2>
              );
            }
            default:
              return `No custom definition for ${field.name} with control: ${CustomComponentConfig?.control} || ${field.control}`;
          }
        }

        return `No component definition for ${field.control}`;
      }
    }
  };

  return FieldGridRenderer({ schema, renderField });
}

export default FieldGeneratorV2;
